// custom-attendees-manager.component.ts
import { CommonModule } from '@angular/common';
import {
    Component,
    forwardRef,
    input,
    output,
    ChangeDetectorRef,
    OnInit,
    ViewChild,
    ElementRef,
    OnDestroy,
    HostListener
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { Observable, Subject, of, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { leadsServices } from 'src/app/services/lead.service';

@Component({
    selector: 'app-custom-attendees-manager',
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomAttendeesManagerComponent),
            multi: true,
        }
    ],
    styleUrl: './app-custom-attendees-manager.component.scss',
    template: `
    <div class="custom-attendees-container">
      <label class="attendees-label">{{ label() }}</label>
      <div class="attendees-input-container" [class.focused]="isFocused">
        <div class="attendees-chips">
          <div *ngFor="let attendee of attendees; trackBy: trackById" class="attendee-chip">
            <span class="attendee-name">{{ attendee.name }}</span>
            <button type="button" class="remove-btn" (click)="removeAttendee(attendee)">×</button>
          </div>
          <div class="input-wrapper">
            <input
              #attendeeInput
              class="attendee-input"
              [formControl]="inputCtrl"
              placeholder="Type or search for names/emails"
              (input)="onInputChange(attendeeInput.value)"
              (keydown.enter)="addAttendeeFromInput($event)"
              (keydown.comma)="addAttendeeFromInput($event)"
              (focus)="onFocus()"
              (blur)="onBlur()"
            />
          </div>
        </div>
      </div>
      
      <div *ngIf="showSuggestions && attendeeOptions.length > 0" class="suggestions-container">
        <div 
          *ngFor="let option of attendeeOptions; trackBy: trackById" 
          class="suggestion-item"
          [class.active]="activeIndex === attendeeOptions.indexOf(option)"
          (mousedown)="selectSuggestion(option)"
          (mouseover)="activeIndex = attendeeOptions.indexOf(option)"
        >
          {{ option.name }}
        </div>
      </div>
      
      <div *ngIf="showEmailsOnly()" class="attendees-hint">
        Enter valid email addresses
      </div>
    </div>
    `
})
export class CustomAttendeesManagerComponent implements ControlValueAccessor, OnInit, OnDestroy {
    label = input<string>('Attendees');
    valueSelected = output<any>();
    showEmailsOnly = input<boolean>(false);
    
    inputCtrl = new FormControl('');
    attendees: { id: string; name: string; }[] = [];
    attendeeOptions: { id: string; name: string; }[] = [];
    
    showSuggestions = false;
    isFocused = false;
    activeIndex = -1;
    
    private destroy$ = new Subject<void>();
    private searchTerms = new Subject<string>();
    
    @ViewChild('attendeeInput') attendeeInput: ElementRef<HTMLInputElement>;
    
    constructor(
        private cdr: ChangeDetectorRef,
        private leadsService: leadsServices // Replace 'any' with your actual service type
    ) {}
    
    ngOnInit(): void {
        // Setup search debounce for autocomplete
        this.searchTerms.pipe(
            takeUntil(this.destroy$),
            debounceTime(300),
            distinctUntilChanged(),
            filter(term => !!term),
            switchMap(term => {
                this.showSuggestions = true;
                return of(this.getContacts(term));
            })
        ).subscribe();
    }
    
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    
    // Keyboard navigation for suggestions
    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent): void {
        if (!this.showSuggestions) return;
        
        // Arrow down
        if (event.key === 'ArrowDown' && this.attendeeOptions.length > 0) {
            this.activeIndex = Math.min(this.activeIndex + 1, this.attendeeOptions.length - 1);
            event.preventDefault();
        }
        
        // Arrow up
        if (event.key === 'ArrowUp' && this.attendeeOptions.length > 0) {
            this.activeIndex = Math.max(this.activeIndex - 1, 0);
            event.preventDefault();
        }
        
        // Enter to select highlighted option
        if (event.key === 'Enter' && this.activeIndex >= 0) {
            this.selectSuggestion(this.attendeeOptions[this.activeIndex]);
            event.preventDefault();
        }
        
        // Escape to close suggestions
        if (event.key === 'Escape') {
            this.showSuggestions = false;
            this.activeIndex = -1;
            event.preventDefault();
        }
    }
    
    // ControlValueAccessor implementation
    onChange: any = () => {};
    onTouched: any = () => {};
    
    writeValue(value: any): void {
        if (Array.isArray(value)) {
            this.attendees = value;
            this.cdr.detectChanges();
        } else {
            this.attendees = [];
        }
    }
    
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    
    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.inputCtrl.disable();
        } else {
            this.inputCtrl.enable();
        }
    }
    
    // Focus handling
    onFocus(): void {
        this.isFocused = true;
        if (this.attendeeOptions.length > 0 && this.inputCtrl.value) {
            this.showSuggestions = true;
        }
    }
    
    onBlur(): void {
        this.isFocused = false;
        // Delay hiding suggestions to allow clicking on them
        setTimeout(() => {
            this.showSuggestions = false;
            this.activeIndex = -1;
        }, 150);
    }
    
    // Attendee handling
    addAttendeeFromInput(event: Event): void {
        event.preventDefault();
        
        const value = this.inputCtrl.value?.trim();
        if (value) {
            if (this.showEmailsOnly() && !this.isValidEmail(value)) {
                alert('Please enter a valid email address');
                return;
            }
            
            // Check if the attendee already exists
            if (!this.attendees.some(a => a.name === value)) {
                const newAttendee = {
                    id: `new-${Date.now()}`,
                    name: value
                };
                
                this.attendees.push(newAttendee);
                this.onChange(this.attendees);
                this.valueSelected.emit(newAttendee);
                this.inputCtrl.setValue('');
                this.cdr.detectChanges();
            }
        }
    }
    
    removeAttendee(attendee: { id: string; name: string; }): void {
        const index = this.attendees.findIndex(a => a.id === attendee.id);
        
        if (index >= 0) {
            this.attendees.splice(index, 1);
            this.onChange(this.attendees);
            this.valueSelected.emit(attendee);
            this.cdr.detectChanges();
        }
    }
    
    selectSuggestion(option: { id: string; name: string; }): void {
      // Extract email from the option name if it's in the format "name lastName (email)"
      const emailMatch = option.name.match(/\(([^)]+)\)/);
      const optionEmail = emailMatch ? emailMatch[1] : option.name;
      
      // Check if attendee with same email already exists
      const isDuplicate = this.attendees.some(attendee => {
        // For attendees with only email in name field
        if (!attendee.id) {
          return attendee.name === optionEmail;
        }
        
        // For attendees with name in format "name lastName (email)"
        const attendeeEmailMatch = attendee.name.match(/\(([^)]+)\)/);
        const attendeeEmail = attendeeEmailMatch ? attendeeEmailMatch[1] : attendee.name;
        
        return attendeeEmail === optionEmail || attendee.id === option.id;
      });
      
      if (!isDuplicate) {
        this.attendees.push(option);
        this.onChange(this.attendees);
        this.valueSelected.emit(option);
      }
      
      this.inputCtrl.setValue('');
      this.showSuggestions = false;
      this.activeIndex = -1;
      this.cdr.detectChanges();
    }
    
    // Utility methods
    isValidEmail(email: string): boolean {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }
    
    trackById(index: number, item: { id: string; name: string; }): string {
        return item.id;
    }
    
    onInputChange(value: string): void {
        if (value) {
            this.searchTerms.next(value);
        } else {
            this.showSuggestions = false;
        }
    }
    
    // Data fetching
    async getContacts(search?: string) {
        try {
            const response = await firstValueFrom(
                this.leadsService.get(
                    {
                        pageIndex: 1,
                        pageSize: 5,
                        searchValue: search ? search : '',
                    },
                    'contacts',
                )
            );
            
            this.attendeeOptions = response.data.map((item: any) => ({
                name: `${item.name} ${item.lastName} (${item.email})`,
                id: item.id
            }));
            
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error fetching contacts:', error);
            this.attendeeOptions = [];
        }
    }
}